import type { ErrorCode } from '@koderek/shared';

/** „1 rok”, „3 lata”, „8 lat”, „12 lat”, „22 lata”. */
function years(n: number): string {
  if (n === 1) return '1 rok';
  const lastDigit = n % 10;
  const lastTwo = n % 100;
  return `${n} ${lastDigit >= 2 && lastDigit <= 4 && (lastTwo < 12 || lastTwo > 14) ? 'lata' : 'lat'}`;
}

/** Wszystkie teksty interfejsu w jednym miejscu (NFR-U4). */
export const t = {
  app: {
    name: 'Koderek',
    tagline: 'Największa międzypokoleniowa konferencja edukacyjna',
    poweredBy: 'Powered by',
  },
  /** Stopka na każdym ekranie aplikacji – dane organizatora (docs/Stopka.png, bez linków do mediów społecznościowych). */
  footer: {
    label: 'Organizator',
    logoAlt: 'Fundacja Hybrydowa Szkoła',
    foundation: 'Fundacja Hybrydowa Szkoła',
    address: 'ul. Mickiewicza 37/58 | 01-625 Warszawa',
    registry: 'NIP: 525-287-06-31 | REGON: 389520465 | KRS: 0000911903',
    bank: 'BNP PARIBAS: 63 1600 1462 1862 9340 8000 0001',
    copyright: (year: number) => `© 2022–${year} Koderek.edu.pl projekt Fundacji Hybrydowa Szkoła`,
  },
  nav: {
    start: 'Start',
    login: 'Zaloguj się',
    loginShort: 'Zaloguj',
    register: 'Załóż konto',
    account: 'Moje konto',
    family: 'Moja rodzina',
    familyActivities: 'Moje zapisy',
    qr: 'Kod QR',
    editions: 'Edycje',
    staff: 'Obsługa',
    admin: 'Administracja',
    logout: 'Wyloguj',
    menu: 'Menu',
    families: 'Konta rodzin',
    stats: 'Statystyki',
    speaker: 'Prowadzący',
    adminEditions: 'Edycje',
    logoHome: 'Koderek – strona startowa',
  },
  home: {
    title: 'Witaj w zapisach na Koderka',
    eyebrow: 'Konferencja edukacyjna dla całych rodzin',
    headline: 'Koduj. Buduj. Odkrywaj.',
    intro: 'Wybierz edycję – miasto i datę – na którą się wybierasz.',
    hello: (name: string) => `Cześć, ${name}!`,
    empty: 'Nie ma jeszcze opublikowanych edycji. Zajrzyj tu wkrótce.',
    enrolledIn: (cities: string) => `Masz zapisy: ${cities}`,
    promoTitle: 'Uczyć się można na wiele sposobów',
    promoText: 'Jednodniowa, bezpłatna konferencja: tematyczne warsztaty dla dzieci oraz wykłady i spotkania dla dorosłych. Nauka, która zaczyna się od ciekawości.',
    topics: [
      { title: 'Warsztaty dla dzieci', text: 'Od przedszkolaków po licealistów – eksperymentujemy, tworzymy i odkrywamy nowe rzeczy.' },
      { title: 'Wykłady dla dorosłych', text: 'Trochę wiedzy, trochę inspiracji – o tym, co sprawia, że naprawdę się uczymy.' },
      { title: 'Dla całej rodziny', text: 'Zabierz dziadka, mamę, tatę, brata, siostrę i sąsiada.' },
      { title: 'Każde miejsce – inny program', text: 'Wybierz lokalizację, która najbardziej Was interesuje. Udział jest bezpłatny.' },
    ],
  },
  editions: {
    title: 'Edycje Koderka',
    registrationOpen: 'Zapisy otwarte',
    registrationFrom: (when: string) => `Zapisy od ${when}`,
    earlyAccess: 'Masz wczesny dostęp',
    notOpenYet: (when: string) => `Zapisy na zajęcia ruszają ${when}. Agendę możesz już przejrzeć.`,
    notOpenYetEarly: (when: string) => `Zapisy dla wszystkich ruszają ${when}. Masz wczesny dostęp – możesz zapisywać się już teraz.`,
    registered: 'Masz zapisy',
    finished: 'Edycja zakończona',
    draft: 'Szkic – widoczny tylko dla organizatorów',
    open: 'Zobacz program',
    enroll: 'Zapisz się na zajęcia',
    myPlan: 'Mój plan',
    map: 'Mapa dojazdu',
    mapHint: 'Jak dojechać – Mapy Google',
    floorPlan: 'Plan budynku',
    floorPlanHint: 'PDF – gdzie są sale',
    familySection: 'Kogo zapisujesz?',
    infoHint: 'Ważne przed przyjazdem – rozwiń',
    infoHintOpen: 'Zwiń',
    when: 'Kiedy',
    where: 'Gdzie',
    addPerson: 'Dodaj osobę do rodziny',
    otherCities: 'Inne miejsca tego dnia:',
    loginToRegister: 'Zaloguj się, aby zapisać rodzinę na zajęcia',
    person: 'Osoba',
    past: 'Poprzednie edycje',
    pastEmpty: 'Nie brałeś jeszcze udziału w żadnej edycji.',
    allEditions: 'Wszystkie edycje',
  },
  // Krok „zgłoszenie udziału” usunięty (decyzja 2026‑09‑28) – zostały teksty wspólne.
  registration: {
    you: 'Ty',
    cancel: 'Anuluj',
  },
  participants: {
    title: 'Moja rodzina',
    intro: 'Osoby z Twojego konta. Dane wpisujesz raz – są dostępne we wszystkich edycjach.',
    add: 'Dodaj osobę',
    addTitle: 'Nowa osoba',
    editTitle: 'Zmiana danych',
    edit: 'Zmień',
    editOf: (name: string) => `Zmień dane: ${name}`,
    removeOf: (name: string) => `Usuń: ${name}`,
    guardian: 'Opiekun',
    guardianStudent: 'Ty · uczeń',
    born: (year: number) => `ur. ${year}`,
    save: 'Zapisz',
    kind: 'Kto to jest?',
    kindChild: 'Dziecko',
    kindAdult: 'Dorosły',
    birthYear: 'Rok urodzenia',
    birthYearHint: (min: number, max: number) => `Od ${min} do ${max}.`,
    guardianInfo: 'To Ty. Imię, nazwisko i rok urodzenia (jeśli jesteś uczniem) zmienisz w danych konta.',
    guardianNoRemove: 'Siebie nie można usunąć – możesz usunąć konto.',
    removeTitle: 'Usunąć osobę?',
    removeText: (name: string) => `${name} zostanie usunięta z konta.`,
    removeEditions: 'Zostanie wypisana z zajęć w edycjach:',
    removeConfirm: 'Usuń',
    changedByStaff: 'zmienione przez obsługę',
    afterAdd: 'Osoba dodana – od razu możesz zapisać ją na zajęcia.',
  },
  familyActivities: {
    title: 'Moje zapisy',
    intro: 'Zajęcia, na które zapisane są osoby z Twojej rodziny. Nowe zapisy robisz w agendzie edycji.',
    edition: 'Edycja',
    noEditions: 'Nie ma nadchodzących edycji.',
    noEnrollments: 'Nie macie jeszcze zapisów na zajęcia. Wybierz edycję i zapisz rodzinę w agendzie – tutaj zobaczysz swój plan.',
    toEditions: 'Zobacz edycje',
    toAgenda: 'Przejdź do agendy i zapisz na zajęcia',
    enrolled: 'Zapisane',
    empty: (name: string) => `${name} nie ma jeszcze zapisów w tej edycji.`,
    all: 'Wszyscy',
    emptyAll: 'Nikt z rodziny nie ma jeszcze zapisów w tej edycji.',
    unenrollPerson: (name: string) => `Wypisz: ${name}`,
    unenrollPersonFrom: (name: string, activity: string) => `Wypisz ${name} z: ${activity}`,
    count: (n: number) =>
      n === 1 ? 'jedne zajęcia' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? `${n} zajęcia` : `${n} zajęć`,
    view: 'Widok',
    viewTimeline: 'Plan dnia',
    viewList: 'Lista',
    timelinePromo: 'Zobacz cały dzień rodziny na osi czasu: kto, gdzie i kiedy – oraz wolne okienka z propozycjami, na co jeszcze pójść.',
    timelinePromoButton: 'Pokaż plan dnia',
    timelineHint: 'Kreskowane pola to wolny czas. Kliknij je, aby zobaczyć, na co wtedy można się zapisać i na jaki wykład lub stoisko pójść.',
    you: 'Ty',
    now: 'teraz',
    free: 'wolne',
    suggestions: (n: number) =>
      n === 1 ? '1 propozycja' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? `${n} propozycje` : `${n} propozycji`,
    gapLabel: (name: string, from: string, to: string, suggestions: string) => `${name}, wolne ${from}–${to}: ${suggestions}`,
    background: (from: string, to: string, names: string) => `${names} · ${from}–${to}, równolegle z innymi zajęciami:`,
    gapTitle: (name: string, from: string, to: string) => `${name} · wolne ${from}–${to}`,
    gapEnroll: 'Zajęcia z zapisami w tym czasie',
    gapOpen: 'Wstęp wolny – wykłady, stoiska, pokazy',
    gapNothing: 'W tym czasie nie ma nic, na co ta osoba może pójść.',
    blockLabel: (name: string, activity: string, from: string, to: string) => `${name}: ${activity}, ${from}–${to}`,
  },
  earlyAccess: {
    title: 'Wczesny dostęp',
    working: 'Sprawdzam link…',
    granted: (name: string) => `Masz wczesny dostęp do edycji ${name}. Możesz zapisać rodzinę na zajęcia, zanim zapisy otworzą się dla wszystkich.`,
    alreadyOpen: (name: string) => `Zapisy na ${name} są już otwarte dla wszystkich.`,
    loginIntro: 'Aby skorzystać z wczesnego dostępu, zaloguj się albo załóż konto. Dostęp zostanie przypisany do Twojego konta.',
    toRegistration: 'Przejdź do agendy i zapisz rodzinę',
    missingToken: 'Brak tokenu w linku.',
  },
  admin: {
    editionsTitle: 'Edycje – administracja',
    newEdition: 'Nowa edycja',
    editEdition: 'Edycja',
    back: 'Wróć do listy',
    save: 'Zapisz',
    create: 'Utwórz edycję',
    saved: 'Zapisano.',
    fields: {
      slug: 'Identyfikator w adresie',
      slugHint: 'Np. 2027-krakow. Nie można go zmienić po publikacji.',
      name: 'Nazwa',
      city: 'Miasto',
      address: 'Adres',
      placeName: 'Krótka nazwa miejsca (opcjonalnie)',
      placeNameHint:
        'Np. „SP Kopernika”. Gdy tego samego dnia jest kilka edycji w jednym mieście, przyciski i przełączniki pokażą „Miasto · nazwa” (bez nazwy – adres).',
      mapUrl: 'Link do mapy Google (opcjonalnie)',
      mapUrlHint: 'W Mapach Google: miejsce → Udostępnij → Kopiuj link. Na stronie edycji pojawi się jako ikona mapy przy dacie.',
      floorPlanUrl: 'Link do planu budynku – PDF (opcjonalnie)',
      floorPlanUrlHint: 'Pełny adres pliku dostępnego bez logowania. Pojawi się na stronie edycji, w „Twoim planie” i w przypomnieniu.',
      date: 'Data wydarzenia',
      startTime: 'Rozpoczęcie',
      endTime: 'Zakończenie',
      registrationOpensDate: 'Otwarcie zapisów – data',
      registrationOpensTime: 'Otwarcie zapisów – godzina',
      defaultReserve: 'Domyślna rezerwa miejsc',
      defaultReserveHint: 'Dotyczy zajęć tworzonych później.',
      displayOrder: 'Kolejność na liście',
      displayOrderHint: 'Wśród edycji tego samego dnia – mniejsza liczba wyżej.',
      accentColor: 'Kolor paska na liście edycji',
      accentColorHint: 'Pasek u góry karty edycji. Puste – kolor według stanu zapisów.',
    },
    status: { draft: 'szkic', published: 'opublikowana', finished: 'zakończona' },
    statusActions: {
      published: 'Opublikuj',
      draft: 'Cofnij publikację',
      finished: 'Zakończ edycję',
      reopen: 'Przywróć jako opublikowaną',
    },
    registered: (accounts: number, people: number) => `Rodziny z zapisami lub odprawą: ${accounts}, osoby: ${people}`,
    history: 'Historia statusu',
    earlyTitle: 'Link wczesnego dostępu',
    earlyIntro: 'Wyślij link znajomym. Osoba, która go otworzy, może zapisać rodzinę na zajęcia przed otwarciem zapisów.',
    earlyNone: 'Brak aktywnego linku.',
    earlyGenerate: 'Wygeneruj link',
    earlyRegenerate: 'Wygeneruj nowy (stary przestanie działać)',
    earlyRevoke: 'Unieważnij link',
    earlyCreated: (when: string) => `Utworzony ${when}`,
    earlyAccounts: (n: number) => `Konta z wczesnym dostępem: ${n}`,
    earlyRevokeAccount: 'Odbierz dostęp',
    copy: 'Kopiuj',
    copied: 'Skopiowano',
    colEmail: 'E‑mail',
    colName: 'Imię i nazwisko',
    colGranted: 'Od',
    colPeople: 'Osoby z zapisami',
    colDate: 'Data',
    colOrder: 'Kolejność',
    colCity: 'Miasto',
    colStatus: 'Status',
  },
  consentsAdmin: {
    nav: 'Zgody',
    title: 'Zgody',
    intro:
      'Treści zgód i klauzula informacyjna z formularza rejestracji (także rejestracji na miejscu). Zmiana obowiązuje od razu – bez wersji i historii.',
    required: 'wymagana',
    optional: 'dobrowolna',
    placeholder: 'treść do uzupełnienia',
    edit: 'Zmień treść',
    content: 'Treść zgody',
    contentHint: 'Zwykły tekst (bez formatowania), co najmniej 10 znaków.',
    tooShort: 'Treść musi mieć co najmniej 10 znaków.',
    save: 'Zapisz',
    cancel: 'Anuluj',
    saved: 'Zapisano – formularz rejestracji pokazuje już nową treść.',
    accepted: (n: number) => `Zaakceptowało kont: ${n}`,
    noticeTitle: 'Klauzula informacyjna pod formularzem rejestracji',
    noticeIntro: 'Pokazywana drobnym drukiem pod przyciskiem „Załóż konto”.',
    noticeHint: 'Akapity, pogrubienie, listy i linki. Pusta treść = bez klauzuli.',
    noticeEmpty: 'Brak klauzuli – formularz rejestracji jej nie pokazuje.',
  },
  adminAccounts: {
    title: 'Konta i role',
    nav: 'Konta i role',
    search: 'Szukaj konta',
    searchHint: 'E‑mail, imię lub nazwisko (co najmniej 2 znaki).',
    none: 'Nie znaleziono konta. Możesz zaprosić tę osobę poniżej.',
    tabs: { all: 'Wszystkie', admin: 'Administratorzy', staff: 'Obsługa', speaker: 'Prowadzący', none: 'Rodziny (bez ról)' },
    found: (n: number) => `Kont: ${n}`,
    openFamily: 'Rodzina i zapisy',
    roles: 'Role',
    save: 'Zapisz role',
    saved: 'Role zapisane. Zmiana działa od następnego kliknięcia tej osoby.',
    noPassword: 'bez hasła',
    inviteTitle: 'Zaproś osobę',
    inviteIntro: 'Powstanie konto z wybranymi rolami, a na adres pójdzie link do ustawienia hasła. Jeśli konto już istnieje, dostanie te role.',
    invite: 'Wyślij zaproszenie',
    invited: 'Zaproszenie wysłane.',
    rolesAdded: 'Konto już istniało i ma hasło – role zostały dodane (bez maila).',
    rolesAddedReinvited: 'Konto już istniało, ale bez hasła – role zostały dodane i wysłaliśmy nowy link do ustawienia hasła (poprzedni już nie działa).',
    resendInvitation: 'Wyślij zaproszenie ponownie',
    invitationResent: (email: string) => `Nowy link wysłany na ${email}. Poprzedni już nie działa.`,
    rolesRequired: 'Zaznacz co najmniej jedną rolę.',
  },
  stats: {
    title: 'Statystyki',
    open: 'Statystyki',
    generatedAt: (when: string) => `stan na ${when}`,
    scopeLabel: 'Edycja albo cały dzień',
    scopePlaceholder: 'Wybierz edycję',
    wholeDay: (n: number) => `Wszystkie edycje tego dnia (${n})`,
    dayTitle: (date: string) => `dzień ${date}`,
    statusSuffix: { draft: ' (szkic)', published: '', finished: ' (zakończona)' } as Record<string, string>,
    noEditions: 'Nie ma jeszcze żadnej edycji.',
    colEdition: 'Edycja',
    tabNew: 'Nowe',
    tabLegacy: 'Stare',
    poweredBy: {
      title: 'Kliknięcia „Powered by CodiFive”',
      hint: 'Link w stopce aplikacji – wszystkie kliknięcia, niezależnie od edycji (ostatnie 100 poniżej).',
      total: 'Wszystkich kliknięć',
      loggedIn: 'Zalogowanych',
      anonymous: 'Niezalogowanych',
      accounts: 'Różnych kont',
      when: 'Kiedy',
      who: 'Kto',
      anonymousRow: 'niezalogowany',
      none: 'Nikt jeszcze nie kliknął.',
    },
    legacy: {
      users: 'Liczba użytkowników',
      registrations: 'Liczba rejestracji',
      adults: 'Liczba dorosłych',
      children: 'Liczba dzieci',
      seats: 'Wszystkich miejsc',
      seatsFree: 'Wolnych',
      seatsTaken: 'Zajętych',
      workshopCoverage: 'Pokrycie warsztatów',
      talks: 'Liczba prelekcji',
      workshops: 'Liczba warsztatów',
      empty: 'Pustych warsztatów (nikt się nie zarejestrował)',
      closed: 'Zamkniętych warsztatów',
      coverageFor: (group: string) => `Pokrycie warsztatów dostępnych dla ${group}`,
      coverageAdultsOnly: 'Pokrycie warsztatów tylko dla dorosłych',
      hint:
        'Układ i sposób liczenia jak w starym systemie, dla tej edycji: użytkownicy – rodziny z zapisami, rejestracje – osoby ' +
        'w tych rodzinach, warsztaty – zajęcia z zapisami, miejsca – limit bez rezerwy (dlatego pokrycie może przekroczyć 100%, ' +
        'a wolnych może być mniej niż zero), zamknięte – zapisanych co najmniej tyle, ile wynosi limit, grupa wiekowa – zajęcia ' +
        'obejmujące cały przedział wieku. Odwołane zajęcia pominięte.',
      hintDay:
        'Dla wszystkich edycji dnia użytkownicy to także konta wszystkich prowadzących (również nieaktywowane), nawet bez zapisów – ile osób może przyjść.',
    },
    accounts: 'Rodziny z zapisami',
    earlyAccess: (n: string) => `w tym z wczesnym dostępem: ${n}`,
    children: 'Dzieci w tych rodzinach',
    childrenIdle: (n: string) => `bez żadnych zajęć: ${n}`,
    adults: 'Dorośli w tych rodzinach',
    activities: 'Zajęcia',
    byCategory: (w: number, talk: number, other: number, cancelled: number) =>
      `warsztaty ${w}, prelekcje ${talk}, inne ${other}; odwołane ${cancelled}`,
    occupancy: 'Obłożenie',
    taken: (enrolled: string, total: string) => `zajęte ${enrolled} z ${total}`,
    limit: 'Limit miejsc',
    freeInLimit: (n: string) => `wolne w limicie: ${n}`,
    reserve: 'Rezerwa',
    freeInReserve: (n: string) => `wolne w rezerwie: ${n}`,
    fullEmpty: 'Zajęcia pełne / puste',
    ageGroups: 'Miejsca dla grup wiekowych',
    group: 'Grupa',
    seats: 'Miejsca',
    enrolled: 'Zajęte',
    groups: {
      '3-5': '3–5 lat',
      '6-7': '6–7 lat',
      '8-11': '8–11 lat',
      '12-14': '12–14 lat',
      '15-18': '15–18 lat',
      adults: 'dorośli',
      adults_only: 'tylko dla dorosłych',
    },
    timeline: 'Nowe rodziny i zapisy w czasie',
    daily: 'Dzień po dniu',
    hourly: (day: string) => `Otwarcie zapisów (${day})`,
    registrations: 'nowe rodziny',
    enrollments: 'zapisy',
    pointTitle: (label: string, r: number, e: number) => `${label}: nowe rodziny ${r}, zapisy ${e}`,
    noData: 'Brak zapisów.',
    table: 'Zajęcia',
    filterAll: 'Wszystkie',
    filterFull: 'Pełne',
    filterAlmost: 'Prawie pełne (≥ 90%)',
    filterEmpty: 'Puste',
    colSymbol: 'Symbol',
    colTitle: 'Tytuł',
    colType: 'Rodzaj',
    colRoom: 'Sala',
    colTime: 'Godziny',
    colAge: 'Wiek',
    colEnrolled: 'Zapisani',
    colCapacity: 'Limit + rezerwa',
    adultsShort: 'dorośli',
    anyAge: 'wszyscy',
    freeEntry: 'wstęp wolny',
    cancelled: 'odwołane',
    noRows: 'Brak zajęć dla wybranego filtra.',
    editions: 'Statystyki edycji',
    eventDay: 'Dzień wydarzenia',
    eventDayHint: 'Odświeżane co 30 s.',
    checkedInAccounts: 'Odprawione rodziny',
    checkedInChildren: 'Odprawione dzieci',
    checkedInAdults: 'Odprawieni dorośli',
    ofRegistered: (n: string) => `z ${n} oczekiwanych`,
    notCheckedIn: 'Zapisani na zajęcia, nieodprawieni',
    notCheckedInHint: 'osoby, których zapisy mogą przepaść',
    attendanceTotal: 'Obecni na zajęciach',
    attendanceTotalHint: (enrolled: string, unconfirmed: number) =>
      `z ${enrolled} zapisanych (zajęcia z potwierdzoną listą)${unconfirmed ? `; niepotwierdzone listy: ${unconfirmed}` : ''}`,
    checkInTimeline: 'Odprawy co 15 minut',
    checkInPoint: (label: string, n: number) => `${label}: ${n}`,
    noCheckIns: 'Nikt nie został jeszcze odprawiony.',
    attendanceTable: 'Obecność na zajęciach',
    colAttended: 'Obecni',
    unconfirmed: 'nie potwierdzono',
  },
  publish: {
    title: 'Linki na koderek.edu.pl',
    intro: 'Stałe adresy tej edycji. Link i przycisk prowadzą do agendy, gdzie rodzice logują się i zapisują.',
    draftHint: 'Adresy zaczną działać po opublikowaniu edycji.',
    agendaUrl: 'Adres agendy',
    apiUrl: 'Agenda w JSON (publiczne API)',
    button: 'Przycisk HTML do wklejenia na stronę',
    buttonLabel: 'Zapisz się na Koderka',
    boardUrl: 'Tablica „najbliższe zajęcia” na ekrany w szkole',
    boardHint:
      'Otwórz na ekranie w trybie pełnoekranowym (F11). Tylko wybrane sale: ?sala=Aula,12. Liczba wierszy: ?wiersze=8, stron: ?strony=2. Podgląd o danej godzinie dnia edycji: ?czas=10:45.',
  },
  agenda: {
    title: 'Agenda',
    empty: 'Agenda tej edycji nie jest jeszcze gotowa.',
    filterAge: 'Wiek',
    filterAgeAny: 'Wszystkie',
    filterAdult: 'Dorośli',
    ageRange: (min: number, max: number) => `${min}–${max} lat`,
    filterType: 'Rodzaj zajęć',
    filterTypeAll: 'Wszystkie rodzaje',
    filterFree: 'Tylko z wolnymi miejscami',
    filterAgeLabel: 'Wiek:',
    filterPerson: (name: string, age?: string) => `Pasujące dla: ${name}${age ? ` (${age})` : ''}`,
    filterAgeAnyForPerson: 'Wszystkie grupy wiekowe',
    personAge: (n: number) => years(n),
    personAdult: 'dorosły',
    personHintOn: (name: string, age?: string) =>
      `Pokazujemy tylko zajęcia, na które ${name} pasuje wiekiem${age ? ` (${age})` : ''}. Żeby zobaczyć resztę, wybierz „Wszystkie grupy wiekowe”.`,
    personHintOff: (name: string) => `Pokazujemy zajęcia dla wszystkich grup wiekowych – na zajęcia niepasujące wiekiem ${name} się nie zapisze.`,
    clearFilters: 'Wyczyść filtry',
    none: 'Brak zajęć dla wybranych filtrów.',
    cancelled: 'Odwołane',
    hiddenRoom: 'sala ukryta',
    allDay: 'Przez cały dzień – stoiska i pokazy',
    byHour: 'Według godzin – sale i zajęcia',
    tabEnroll: (n: number) => `Zajęcia z zapisami (${n})`,
    tabOpen: (n: number) => `Zajęcia bez zapisów (otwarte wykłady, stoiska i pokazy) (${n})`,
    layout: 'Widok agendy',
    layoutGrid: 'Tabela',
    layoutList: 'Lista',
    freeEntry: 'Wstęp wolny',
    seats: (free: number, total: number) => `Miejsc: ${free}/${total}`,
    seatsReserve: (free: number, total: number) => `Miejsc z rezerwy: ${free}/${total}`,
    full: 'Brak miejsc',
    details: 'Szczegóły',
    room: 'Sala',
    type: 'Rodzaj',
    age: 'Wiek',
    when: 'Godzina',
    seatsLabel: 'Wolne miejsca',
    fromReserve: 'rezerwa',
    seatsOf: (free: number, total: number) => `${free} z ${total}`,
    about: 'O zajęciach',
    with: 'Prowadzi:',
    speakers: 'Prowadzący',
    close: 'Zamknij',
    back: 'Wróć do agendy',
    enrollSoon: 'Zapisy na zajęcia pojawią się wkrótce.',
    loginToEnroll: 'Zaloguj się, aby zapisać',
    notFound: 'Nie ma takich zajęć w tej edycji.',
    cancelReason: (reason: string) => `Zajęcia odwołane. Powód: ${reason}`,
  },
  enrollment: {
    enroll: 'Zapisz',
    enrolled: 'Zapisany',
    unenroll: 'Wypisz',
    unenrollTitle: 'Wypis z zajęć',
    unenrollQuestion: (name: string, activity: string) => `Wypisać ${name} z ${activity}?`,
    unenrollConfirm: 'Tak, wypisz',
    unenrollStarted: 'Wypis niemożliwy – zajęcia się rozpoczęły',
    byStaff: 'zapisane przez obsługę',
    enrollFor: (name: string) => `Zapisz: ${name}`,
    planEmpty: 'Brak zapisów. Wybierz zajęcia z agendy i kliknij „Zapisz”.',
    planTitle: (name: string) => `Plan: ${name}`,
    choosePerson: 'Wybierz osobę, aby zobaczyć, na co może się zapisać.',
    staffAgenda: 'Agenda i zapisy',
    person: 'Osoba',
    reasons: {
      cancelled: 'Zajęcia odwołane',
      no_enrollment: 'Wstęp wolny',
      ended: 'Zajęcia zakończone',
      edition_closed: 'Zapisy zamknięte',
      not_open_yet: (when: string) => `Zapisy od ${when}`,
      other_edition: (name: string, edition: string) => `${name} ma już zapisy tego dnia w innym mieście: ${edition}`,
      age: (range: string, name: string, age: number | undefined) =>
        age === undefined ? `Dla wieku ${range}` : `Dla wieku ${range}, ${name} ma ${age} lat`,
      children_only: 'Tylko dla dzieci',
      adults_only: 'Tylko dla dorosłych',
      collision: (activity: string) => `Koliduje z: ${activity}`,
      travel_buffer: (activity: string, minutes: number) =>
        `Za mało czasu na przejście (min. ${minutes} min) – zajęcia w innym miejscu: ${activity}`,
      full: 'Brak miejsc',
    },
  },
  plan: {
    send: 'Wyślij mi plan na e-mail',
    sendToFamily: 'Wyślij plan rodzinie',
    sent: (email: string) => `Wysłano na ${email}`,
    tooSoon: (minutes: number) => `Plan został wysłany przed chwilą. Ponownie za ${minutes} min.`,
  },
  program: {
    title: 'Program edycji',
    open: 'Program',
    tabActivities: 'Zajęcia',
    tabRooms: 'Sale',
    tabSpeakers: 'Prowadzący',
    typesTitle: 'Rodzaje zajęć',
    typesLink: 'Słownik rodzajów zajęć',
    planMailInfo: {
      title: 'Maile do rodzin',
      body:
        'Zapis ani zmiana zajęć nie wysyłają maila od razu. Każda zmiana (zapis, wypis, zmiana lub odwołanie zajęć tutaj) przesuwa wysyłkę „Twojego planu” o PLAN_MAIL_DELAY_MINUTES (domyślnie 20 min) – rodzina dostaje jeden mail po ostatniej zmianie, tylko gdy plan się różni od wysłanego.',
    },
    addActivity: 'Dodaj zajęcia',
    editActivity: 'Zmień zajęcia',
    newActivity: 'Nowe zajęcia',
    save: 'Zapisz',
    cancel: 'Odwołaj',
    cancelTitle: 'Odwołanie zajęć',
    cancelText: (n: number) => `Zapisani (${n}) zostaną wypisani i dostaną maila z powodem odwołania.`,
    cancelReason: 'Powód odwołania',
    cancelConfirm: 'Odwołaj zajęcia',
    restore: 'Przywróć',
    remove: 'Usuń',
    removeTitle: 'Usunąć zajęcia?',
    removeText: 'Zajęcia znikną z agendy. Na te zajęcia nikt się nie zapisywał.',
    history: 'Historia',
    /** Podpowiedzi przy przyciskach w tabeli zajęć – konsekwencje dla zapisanych (US-305, US-306). */
    tips: {
      edit:
        'Zapisani zostają na zajęciach – ich zapisy przesuwają się razem z nowymi godzinami. Zmiana godziny, sali lub tytułu ' +
        'planuje rodzinom zapisanych nowy mail „Twój plan” (po kilku minutach od ostatniej zmiany; osobnego maila o zmianie nie ma). ' +
        'Zmiana jest odrzucana w całości, jeśli wykluczyłaby kogoś z zapisanych: kolizja godzin z innymi zapisami, limit z rezerwą ' +
        'poniżej liczby zapisanych, wiek lub grupa, typ bez zapisów. Symbolu zajęć z zapisami nie można zmienić. ' +
        'Uwaga: zmiana jest możliwa także dla zajęć, które już się zaczęły.',
      cancel:
        'Wszyscy zapisani zostaną wypisani, a każde konto z zapisem dostanie od razu maila z powodem odwołania; rodzinom zostanie też ' +
        'zaplanowany nowy „Twój plan”. Zajęcia zostają w agendzie jako „Odwołane” z powodem. Uwaga: przy zajęciach, które już się ' +
        'odbyły lub trwają, znika też zaznaczona obecność.',
      restore:
        'Zapisy znów będą możliwe, ale wcześniej wypisani nie wracają automatycznie i nie dostają żadnego maila.',
      remove:
        'Zajęcia znikną z agendy na zawsze. Dostępne tylko dla zajęć, na które nikt nigdy się nie zapisał – nikt nie dostaje maila.',
    },
    historyEmpty: 'Brak zmian.',
    historyAction: { create: 'utworzenie', update: 'zmiana', cancel: 'odwołanie', restore: 'przywrócenie' },
    historySource: { panel: 'panel', import: 'import' },
    enrolled: (enrolled: number, capacity: number, reserve: number) => `${enrolled} / ${capacity} + ${reserve}`,
    fields: {
      symbol: 'Symbol',
      title: 'Tytuł',
      type: 'Rodzaj',
      room: 'Sala',
      roomHint: 'Wybierz albo wpisz nową – zostanie utworzona.',
      startTime: 'Początek',
      endTime: 'Koniec',
      audience: 'Dla kogo',
      ageMin: 'Wiek od (opcjonalnie)',
      ageMax: 'Wiek do (opcjonalnie)',
      capacity: 'Limit miejsc',
      capacityHint: '0 = wstęp wolny, bez zapisów.',
      reserve: 'Rezerwa',
      reserveHint: 'Puste = domyślna z edycji.',
      description: 'Opis',
      descriptionHint: 'Można użyć podstawowych znaczników HTML: <p>, <b>, <i>, <ul>, <li>, <a>.',
      requirements: 'Wymagania prowadzącego (np. rzutnik, 10 laptopów) – tylko dla organizatorów, uczestnicy ich nie widzą',
      speakersDisplay: 'Prowadzący do wyświetlenia (opcjonalnie)',
      speakers: 'Prowadzący (konta)',
      speakerEmail: 'E‑mail',
      addSpeaker: 'Dodaj prowadzącego',
      removeSpeaker: 'Usuń',
      speakerBio: 'Bio (opcjonalnie)',
    },
    fieldNames: {
      symbol: 'symbol',
      title: 'tytuł',
      type: 'rodzaj',
      room: 'sala',
      startTime: 'początek',
      endTime: 'koniec',
      audience: 'dla kogo',
      ageMin: 'wiek od',
      ageMax: 'wiek do',
      capacity: 'limit',
      reserve: 'rezerwa',
      description: 'opis',
      requirements: 'wymagania',
      speakersDisplay: 'prowadzący (tekst)',
      speakers: 'prowadzący',
      email: 'e‑mail',
      firstName: 'imię',
      lastName: 'nazwisko',
    } as Record<string, string>,
    audience: { children: 'dzieci', children_adults: 'dzieci i dorośli', adults: 'tylko dorośli' },
    conflicts: {
      capacity_below_enrolled: 'limit z rezerwą mniejszy niż liczba zapisanych',
      age_excludes_enrolled: 'wiek lub grupa wyklucza zapisanych',
      time_collision: 'nowy czas koliduje z innymi zapisami albo jest za blisko zajęć w innym miejscu',
      type_without_enrollment: 'rodzaj bez zapisów, a są zapisani',
      symbol_locked: 'symbolu zajęć z zapisami nie można zmienić',
    } as Record<string, string>,
    colSymbol: 'Symbol',
    colTime: 'Godziny',
    colTitle: 'Tytuł',
    colSpeakers: 'Prowadzący',
    colRoom: 'Sala',
    colAge: 'Wiek',
    colEnrolled: 'Zapisani / limit + rezerwa',
    colStatus: 'Stan',
    active: 'aktywne',
    cancelled: 'odwołane',
    empty: 'Brak zajęć. Dodaj je ręcznie albo zaimportuj agendę.',
    rooms: {
      add: 'Dodaj salę',
      name: 'Nazwa sali',
      hidden: 'Ukryta w agendzie',
      hiddenHint: 'Ukryta sala i jej zajęcia nie są widoczne dla rodziców.',
      up: 'Wyżej',
      down: 'Niżej',
      remove: 'Usuń',
      activities: (n: number) => `zajęcia: ${n}`,
      save: 'Zapisz',
    },
    speakers: {
      empty: 'Brak prowadzących w tej edycji.',
      intro:
        'Import agendy i dodanie prowadzącego w zajęciach zakładają konto bez maila. Zaproszenia (link do ustawienia hasła) wyślij stąd, gdy agenda będzie gotowa.',
      status: {
        not_invited: 'nie zaproszony',
        invited: (at: string) => `zaproszony ${at}`,
        active: 'konto aktywne',
      },
      invite: 'Wyślij zaproszenie',
      resend: 'Wyślij zaproszenie ponownie',
      sent: 'Zaproszenie wysłane.',
      inviteAll: (n: number) => `Wyślij zaproszenia (${n})`,
      allInvited: 'Wszyscy prowadzący dostali już zaproszenie.',
      confirmTitle: 'Wysłać zaproszenia?',
      confirmText: (n: number) =>
        `Zaproszenie z linkiem do ustawienia hasła dostanie ${n} ${n === 1 ? 'prowadzący' : 'prowadzących'} – wszyscy, którzy jeszcze go nie dostali.`,
      confirm: 'Wyślij',
      cancel: 'Anuluj',
      sentAll: (n: number) => (n ? `Wysłano zaproszenia: ${n}.` : 'Nie było komu wysłać – wszyscy dostali już zaproszenie.'),
    },
    import: {
      nav: 'Import agendy',
      title: 'Import agendy',
      link: 'Import agendy z arkusza',
      intro:
        'W arkuszu agendy wybierz zakładkę edycji i pobierz ją: Plik → Pobierz → Wartości rozdzielone przecinkami (.csv). Kolumny są rozpoznawane po nagłówku (od, do, Sala, -, Typ, Tytuł, Opis, Imię, Nazwisko, email…). Zajęcia są dopasowywane po symbolu – ponowny import aktualizuje istniejące i dodaje nowe.',
      edition: 'Edycja',
      editionPlaceholder: 'Wybierz edycję',
      noEditions: 'Brak edycji, do których można importować (zakończonych nie można zmieniać).',
      file: 'Wybierz plik CSV',
      fileChosen: (name: string) => `Plik: ${name}`,
      noFile: 'Nie wybrano pliku.',
      dryRun: 'Tryb próbny – tylko sprawdź, nic nie zapisuj',
      removeMissing: 'Zastąp agendę – usuń zajęcia, których nie ma w pliku',
      removeMissingHint:
        'Usuwane są tylko zajęcia, na które nikt się nie zapisywał. Zajęcia z zapisami zostają i trafiają do raportu – można je odwołać w programie edycji.',
      run: 'Importuj',
      runDry: 'Sprawdź plik',
      success: 'Import zakończony.',
      successDry: 'Tryb próbny: plik jest poprawny. Nic nie zostało zapisane – odznacz tryb próbny, żeby zaimportować.',
      failed: 'Import odrzucony – nic nie zostało zapisane. Popraw błędy w arkuszu (albo w aplikacji) i wgraj plik ponownie:',
      fixHint: 'Nieznany rodzaj zajęć? Dodaj nazwę alternatywną w słowniku rodzajów zajęć. Inna nazwa sali? Zmień ją w programie edycji albo w arkuszu.',
      created: 'Nowe',
      updated: 'Zmienione',
      unchanged: 'Bez zmian',
      removed: 'Usunięte (brak w pliku)',
      missing: 'Brak w pliku – zostawione',
      newRooms: 'Nowe sale (zostaną dodane)',
      newSpeakers: 'Nowi prowadzący (konta bez maila – zaproszenia wyślesz z zakładki „Prowadzący” w programie edycji)',
      history: 'Historia importów',
      historyEmpty: 'Nie było jeszcze importów tej edycji.',
      source: { json: 'API (JSON)', legacy: 'stary format', panel: 'agenda', co_speakers: 'współprowadzący' },
      dryLabel: 'próbny',
      ok: 'udany',
      bad: 'odrzucony',
      counts: (created: number, updated: number, errors: number) => `nowe ${created}, zmienione ${updated}, błędy ${errors}`,
      coCounts: (added: number, errors: number) => `dopisani ${added}, błędy ${errors}`,
      modes: { agenda: 'Agenda', coSpeakers: 'Współprowadzący' },
      co: {
        rules: [
          {
            title: 'Plik',
            items: [
              'CSV z zakładki „Prowadzący” arkusza: kolumny Kod zajęć (albo Symbol), Imię, Nazwisko, E-mail – w dowolnej kolejności, inne kolumny (np. Telefon) są pomijane.',
              'Bez nagłówka – kolumny dokładnie w kolejności Imię, Nazwisko, Email, Symbol. Separator przecinek albo średnik.',
              'W komórce kodu może być kilka zajęć po przecinku, np. „W37, W38, W39”.',
              'Jeden plik obejmuje wszystkie edycje dnia wybranej edycji – kody są szukane w każdej z nich.',
            ],
          },
          {
            title: 'Co robi import',
            items: [
              'Dopisuje osoby do prowadzących zajęć – nikogo nie odpina i nie zmienia tekstu „prowadzący do wyświetlenia”.',
              'Zakłada konta z rolą prowadzącego bez wysyłania maila – zaproszenia wyślesz z zakładki „Prowadzący” w programie edycji.',
              'Ta sama osoba w kilku wierszach (ten sam e‑mail, imię i nazwisko) to jedno konto.',
            ],
          },
          {
            title: 'Pomijane osoby (reszta pliku się importuje)',
            items: [
              'Bez e‑maila – zostają tylko w tekście „prowadzący do wyświetlenia”.',
              'Z błędnym e‑mailem albo z literówką w domenie (np. gnail.com, wp.p) – z podpowiedzią poprawnego adresu.',
              'Z e‑mailem wpisanym w pliku u kilku różnych osób (np. adres rodzica albo nauczyciela) – konta nie dostaje nikt z nich.',
              'Z e‑mailem, który ma już konto innej osoby (inne imię lub nazwisko).',
            ],
          },
          {
            title: 'Błędy – import odrzucony w całości',
            items: [
              'Kod zajęć, którego nie ma w żadnej edycji tego dnia, albo kod obecny w kilku edycjach dnia.',
              'Brak kodu zajęć, imienia lub nazwiska przy osobie z e‑mailem.',
            ],
          },
        ],
        warning:
          'Ponowny import agendy ustawia prowadzących zajęć według arkusza – współprowadzący zostaną wtedy odpięci (konta zostają). Po każdym imporcie agendy wgraj ten plik jeszcze raz.',
        fixHint:
          'Nieznany kod? Sprawdź, czy zajęcia są już w agendzie którejś edycji tego dnia (najpierw import agendy). Kod w kilku edycjach dnia? Zmień go w arkuszu agendy, żeby był jednoznaczny.',
        warnings: (n: number) => `Pominięte osoby z wątpliwym e‑mailem: ${n}`,
        warningsHint:
          'Tych osób nie zaimportowano (pozostałe – tak). Popraw e‑mail w arkuszu i wgraj plik ponownie albo dopisz osobę ręcznie w programie edycji. E‑mail wspólny dla kilku osób (np. adres rodzica albo nauczyciela) nie zakłada konta nikomu z nich.',
        editions: 'Edycje dnia objęte importem',
        added: 'Dopisani do zajęć',
        alreadyAssigned: 'Już prowadzili te zajęcia',
        newSpeakers: 'Nowe konta (bez maila – zaproszenia z zakładki „Prowadzący” w programie edycji)',
        withoutEmail: 'Bez e‑maila – pominięci',
      },
      show: 'Raport',
    },
    types: {
      add: 'Dodaj rodzaj',
      name: 'Nazwa',
      color: 'Kolor',
      hasEnrollment: 'Z zapisami',
      blocksTime: 'Blokuje czas (kolizje)',
      statsCategory: 'Kategoria statystyk',
      aliases: 'Nazwy alternatywne w imporcie (po przecinku)',
      categories: { workshop: 'warsztat', talk: 'prelekcja', other: 'inne' },
      used: (n: number) => `zajęcia: ${n}`,
      edit: 'Zmień',
      remove: 'Usuń',
      save: 'Zapisz',
      yes: 'tak',
      no: 'nie',
    },
  },
  materials: {
    title: 'Materiały edycji',
    intro:
      'Linki do plików i stron dla uczestników, np. mapa i instrukcja labiryntu zagadek. Pokazują się jako kafelki na stronie edycji (obok mapy i planu budynku) i jako lista w przypomnieniu przed wydarzeniem. Plik trzymaj na zewnątrz (np. koderek.edu.pl), tu wklej jego adres.',
    none: 'Brak materiałów.',
    add: 'Dodaj materiał',
    edit: 'Zmień',
    remove: 'Usuń',
    cancel: 'Anuluj',
    save: 'Zapisz',
    fieldTitle: 'Nazwa',
    titlePlaceholder: 'Labirynt zagadek',
    fieldHint: 'Podpis (opcjonalnie)',
    hintHint: 'Krótko, co w środku – pojawi się pod nazwą na kafelku.',
    hintPlaceholder: 'Mapa i instrukcja (PDF)',
    fieldUrl: 'Link',
    urlHint: 'Pełny adres pliku lub strony dostępnej bez logowania.',
    defaultHint: 'Otwórz w nowej karcie',
    moveUp: (title: string) => `Przesuń wyżej: ${title}`,
    moveDown: (title: string) => `Przesuń niżej: ${title}`,
    removeTitle: 'Usunąć materiał?',
    removeConfirm: (title: string) => `„${title}” zniknie ze strony edycji i z kolejnych przypomnień.`,
  },
  partners: {
    nav: 'Partnerzy',
    title: 'Partnerzy wydarzenia',
    intro: 'Logotypy na dole strony startowej i listy edycji – wspólne dla wszystkich edycji. Kolejność sekcji jest stała, kolejność w sekcji ustawiasz strzałkami.',
    sectionLabel: 'Partnerzy wydarzenia',
    categories: {
      organizer: 'Organizatorzy',
      honorary_patron: 'Honorowy patronat',
      sponsor: 'Sponsorzy konferencji',
      partner: 'Partnerzy wydarzenia',
      patron: 'Patroni wydarzenia',
    },
    add: 'Dodaj partnera',
    edit: 'Zmień',
    remove: 'Usuń',
    cancel: 'Anuluj',
    save: 'Zapisz',
    none: 'Brak partnerów w tej sekcji.',
    noUrl: 'bez linku',
    category: 'Rodzaj',
    name: 'Nazwa partnera',
    url: 'Link do partnera',
    urlHint: 'Adres strony partnera (https://…); puste = logo bez linku.',
    logo: 'Logo',
    logoReplace: 'Nowe logo (puste = zostaje obecne)',
    logoHint: 'PNG, JPEG, WebP, GIF albo SVG, najwyżej 1 MB. Najlepiej z przezroczystym tłem.',
    logoTooBig: 'Plik ma więcej niż 1 MB.',
    moveUp: (name: string) => `Przesuń wyżej: ${name}`,
    moveDown: (name: string) => `Przesuń niżej: ${name}`,
    removeTitle: 'Usunąć partnera?',
    removeConfirm: (name: string) => `Partner „${name}” zniknie ze strony startowej i listy edycji.`,
  },
  landing: {
    nav: 'Strona startowa',
    title: 'Strona startowa',
    frameTitle: 'Koderek – strona startowa',
    intro:
      'Cały dokument HTML (np. od grafika) wyświetlany pod adresem głównym aplikacji. Na następną edycję wklej nowy HTML. Pusta treść = pod adresem głównym lista edycji.',
    field: 'HTML strony startowej',
    linksTitle: 'Linki do edycji',
    links:
      'Link do edycji: href="/edycje/ADRES", np. <a href="/edycje/koderek-test">. Bez target="_blank" link otwiera się w tym samym oknie aplikacji; z target="_blank" – w nowej karcie. ADRES to identyfikator z adresu strony edycji (panel edycji).',
    limits:
      'Style, czcionki (np. Google Fonts), obrazki z internetu i skrypty inline działają. Strona jest w piaskownicy: nie ma dostępu do sesji ani danych aplikacji, formularze nie działają.',
    save: 'Zapisz',
    saved: 'Zapisano – strona startowa jest już widoczna pod adresem głównym.',
    cleared: 'Zapisano pustą treść – pod adresem głównym jest lista edycji.',
    preview: 'Podgląd zapisanej wersji',
    open: 'Otwórz stronę startową',
    empty: 'Brak strony startowej.',
  },
  mailFooter: {
    nav: 'Stopka maili',
    title: 'Stopka maili',
    intro: 'Jedna stopka pod każdym mailem z aplikacji – rejestracja, hasła, zaproszenia, plany, przypomnienia i kampanie, niezależnie od edycji.',
    hint: 'Akapity, pogrubienie, listy i linki. Można też wkleić gotowy kod HTML – zostaną z niego akapity, pogrubienia, listy i linki. Pusta treść = maile bez stopki.',
    edit: 'Zmień stopkę',
    save: 'Zapisz',
    cancel: 'Anuluj',
    saved: 'Zapisano – nowe maile mają już tę stopkę (wysłanych wcześniej to nie zmienia).',
    empty: 'Brak stopki – maile wychodzą bez niej.',
  },
  settings: {
    nav: 'Ustawienia',
    title: 'Ustawienia',
    intro: 'Ogólne zasady zapisów na kilka edycji tego samego dnia – wspólne dla wszystkich edycji.',
    blockOtherCity: 'Blokuj zapisy w innym mieście tego samego dnia',
    blockOtherCityHint:
      'Osoba z zapisem w edycji w jednym mieście nie zapisze się tego dnia w edycji w innym mieście. Wyłączone – wszystkie edycje dnia można łączyć jak edycje jednego miasta. Miasto porównujemy po polu „Miasto” w danych edycji (bez wielkości liter).',
    travelBuffer: 'Czas na przejście między miejscami (minuty)',
    travelBufferHint:
      'Najmniejsza przerwa między zajęciami tej samej osoby w dwóch edycjach tego dnia. 0 – wystarczy, że zajęcia się nie nakładają. Dotyczy nowych zapisów i zmian godzin zajęć – istniejących zapisów nie zmienia.',
    save: 'Zapisz',
    saved: 'Zapisano – nowe zasady działają od razu.',
    updatedAt: (when: string) => `Ostatnia zmiana: ${when}`,
  },
  mailLog: {
    nav: 'Log wysyłek',
    title: 'Log wysyłek',
    intro: 'Każdy mail z aplikacji: kiedy powstał, czy wyszedł i z jakim błędem. Nieudane wysyłki są ponawiane automatycznie.',
    email: 'E‑mail odbiorcy',
    emailHint: 'Wystarczy fragment adresu.',
    category: 'Rodzaj',
    status: 'Status',
    edition: 'Edycja',
    campaign: 'Kampania',
    from: 'Od dnia',
    to: 'Do dnia',
    any: 'Wszystkie',
    clear: 'Wyczyść filtry',
    categories: { transactional: 'transakcyjny', plan: 'plan', reminder: 'przypomnienie', campaign: 'kampania' },
    statuses: {
      queued: 'w kolejce',
      retrying: 'ponawiany',
      sending: 'wysyłanie',
      sent: 'wysłany',
      failed: 'błąd',
      bounced: 'odbity',
    },
    colCreated: 'Utworzony',
    colTo: 'Odbiorca',
    colSubject: 'Temat',
    colCategory: 'Rodzaj',
    colStatus: 'Status',
    total: (n: number) => `Maili: ${n}`,
    empty: 'Brak maili dla wybranych filtrów.',
    details: 'Szczegóły maila',
    createdAt: 'Utworzony',
    sentAt: 'Wysłany',
    attempts: 'Próby',
    error: 'Ostatni błąd',
    resend: 'Wyślij ponownie',
    resent: 'Mail wrócił do kolejki.',
    htmlVersion: 'Treść',
    textVersion: 'Wersja tekstowa',
    prev: 'Poprzednia',
    next: 'Następna',
    page: (page: number, pages: number) => `Strona ${page} z ${pages}`,
    accountTitle: 'Ostatnie maile',
    accountEmpty: 'Do tego konta nie wysłano jeszcze maili.',
  },
  feedback: {
    // Uczestnik (US-708)
    button: 'Twoja opinia',
    title: 'Podziel się opinią',
    intro: 'Każdą opinię czytają organizatorzy. Dzięki nim kolejne edycje będą lepsze.',
    kindLabel: 'Czego dotyczy?',
    kinds: { positive: 'Co się podobało', problem: 'Problem lub błąd', improvement: 'Czego brakuje, co poprawić' },
    bodyLabel: 'Twoja opinia',
    placeholders: {
      positive: 'Co było fajne? Które zajęcia, co w aplikacji…',
      problem: 'Co nie zadziałało? Gdzie to było i co robiłeś(-aś)?',
      improvement: 'Czego zabrakło? Co zrobić inaczej?',
    },
    send: 'Wyślij opinię',
    sendMany: (n: number) => `Wyślij opinie (${n})`,
    multiHint: 'Możesz wypełnić kilka zakładek – tekst zostaje po przełączeniu, a wyślemy wszystkie naraz.',
    alsoSending: (kinds: string) => `Razem z tą opinią wyślemy też: ${kinds}.`,
    filled: 'wypełnione',
    emptyAll: 'Wpisz swoją opinię.',
    cancel: 'Anuluj',
    thanks: 'Dziękujemy! Przeczytamy każdą opinię.',
    close: 'Zamknij',
    // Admin (US-709)
    nav: 'Opinie',
    adminTitle: 'Opinie uczestników',
    adminIntro:
      'Opinie wysłane z ekranu zapisów. O nowych opiniach admini dostają mail najwyżej raz na godzinę (8:00–24:00). Oznacz przeczytane jako obejrzane, a sprawy rozwiązane jako załatwione.',
    kind: 'Rodzaj',
    status: 'Status',
    edition: 'Edycja',
    noEdition: 'Bez edycji (inne ekrany)',
    search: 'Szukaj',
    searchHint: 'W treści, notatce, nazwisku lub e‑mailu autora.',
    any: 'Wszystkie',
    clear: 'Wyczyść filtry',
    statusFilters: { open: 'Do obejrzenia i obejrzane', all: 'Wszystkie', new: 'Nowe', seen: 'Obejrzane', resolved: 'Załatwione' },
    statuses: { new: 'nowa', seen: 'obejrzana', resolved: 'załatwiona' },
    kindsShort: { positive: 'podobało się', problem: 'problem', improvement: 'brak / do poprawy' },
    total: (n: number) => `Opinii: ${n}`,
    empty: 'Brak opinii dla wybranych filtrów.',
    selectAll: 'Zaznacz wszystkie na stronie',
    select: 'Zaznacz opinię',
    markSelectedSeen: (n: number) => `Oznacz jako obejrzane (${n})`,
    markSeen: 'Obejrzana',
    markResolved: 'Załatwiona',
    markNew: 'Przywróć jako nową',
    author: 'Autor',
    deletedAccount: 'konto usunięte',
    page: 'Strona',
    browser: 'Przeglądarka',
    reply: 'Odpisz mailem',
    note: 'Notatka (widzą tylko admini)',
    saveNote: 'Zapisz notatkę',
    noteSaved: 'Zapisano.',
    history: 'Historia',
    historyLine: (from: string, to: string, by: string | null, at: string) => `${at}: ${from} → ${to}${by ? ` (${by})` : ''}`,
    newCount: (n: number) => `${n} nowych opinii`,
    prev: 'Poprzednia',
    next: 'Następna',
  },
  communication: {
    open: 'Treści i przypomnienia',
    title: 'Treści i przypomnienia',
    back: 'Wróć do edycji',
    contentTitle: 'Treści edycji',
    contentIntro:
      'Informacje organizacyjne i zastrzeżenia widać na stronie edycji i w przypomnieniu. Stopkę maili (wspólną dla wszystkich) ustawisz w „Log wysyłek”.',
    info: 'Informacje organizacyjne',
    infoHint: 'Dojazd, wejście, co zabrać.',
    notice: 'Zastrzeżenia',
    noticeHint: 'Np. informacja o fotografowaniu.',
    saveContent: 'Zapisz treści',
    saved: 'Zapisano.',
    remindersTitle: 'Harmonogram przypomnień',
    remindersIntro:
      'O każdym terminie wszystkie konta z zapisami w tej edycji dostaną mail z informacjami organizacyjnymi, planem rodziny i kodem QR. Przypomnienia idą tylko dla opublikowanej edycji.',
    noReminders: 'Brak terminów – rodziny nie dostaną przypomnienia.',
    date: 'Data',
    time: 'Godzina',
    add: 'Dodaj termin',
    edit: 'Zmień',
    save: 'Zapisz',
    cancel: 'Anuluj',
    remove: 'Usuń',
    states: { pending: 'czeka', sending: 'wysyłanie', sent: 'w kolejce / wysłane', skipped: 'pominięte' },
    progress: (sent: number, queued: number, failed: number) => `wysłane ${sent} · w kolejce ${queued} · błędy ${failed}`,
    previewTitle: 'Podgląd przypomnienia',
    previewIntro:
      'Przypomnienie jest inne dla każdej rodziny – ma jej plan zajęć i kod QR. „Pokaż podgląd” wyświetla poniżej mail dokładnie tak, jak dostanie go wybrane konto (nic nie wysyła). „Wyślij próbny mail do mnie” wysyła tę samą treść na Twój adres z dopiskiem [PRÓBA].',
    previewFor: 'Dla konta (e‑mail)',
    previewForHint: 'Puste – pierwsze konto z zapisami, a gdy nikt się jeszcze nie zapisał – Twoje konto.',
    preview: 'Pokaż podgląd',
    sendTest: 'Wyślij próbny mail do mnie',
    testSent: (email: string) => `Próbny mail wysłany na ${email}.`,
    previewRecipient: (email: string) => `Treść dla konta ${email}`,
  },
  campaigns: {
    nav: 'Kampanie',
    title: 'Kampanie mailowe',
    new: 'Nowa kampania',
    empty: 'Nie ma jeszcze kampanii.',
    back: 'Wróć do listy kampanii',
    kinds: { organizational: 'organizacyjna', marketing: 'marketingowa' },
    kindHint: 'Marketingowa trafi tylko do kont ze zgodą na newsletter i ma link „Wypisz się z newslettera”.',
    statuses: { draft: 'szkic', scheduled: 'zaplanowana', sending: 'w trakcie wysyłki', paused: 'wstrzymana', sent: 'wysłana' },
    colName: 'Nazwa',
    colKind: 'Rodzaj',
    colStatus: 'Status',
    colRecipients: 'Odbiorcy',
    colSent: 'Wysłane',
    colFailed: 'Błędy',
    colStarted: 'Wysyłka',
    name: 'Nazwa',
    nameHint: 'Widzą ją tylko organizatorzy.',
    kind: 'Rodzaj',
    edition: 'Edycja',
    editionHint: 'Do wstawek {nazwa edycji} i {link do planu} oraz stopki edycji.',
    editionNone: 'bez edycji',
    subject: 'Temat',
    body: 'Treść',
    placeholders: 'Wstawki w temacie i treści: {imię}, {nazwa edycji}, {link do planu}.',
    audienceTitle: 'Odbiorcy',
    audienceIntro: 'Mail dostanie każde konto, które spełnia którekolwiek kryterium – najwyżej jeden raz.',
    registeredEditions: 'Z zapisami w edycji',
    checkedInEditions: 'Odprawieni w edycji',
    checkedInEditionsHint: 'Rodziny, które przyszły (ktoś z konta odprawiony na wejściu) – np. ankieta po wydarzeniu.',
    speakerEditions: 'Prowadzący zajęcia w edycji',
    speakerEditionsHint: 'Prowadzący nieodwołanych zajęć – także ci, którzy nie przyjęli jeszcze zaproszenia do konta.',
    activitiesEdition: 'Zajęcia z edycji',
    activities: 'Zapisani na zajęcia',
    newsletter: 'Wszyscy ze zgodą na newsletter',
    manualEmails: 'Adresy wpisane ręcznie',
    manualEmailsHint:
      'Oddzielone przecinkiem, średnikiem albo nową linią – także osoby bez konta (wtedy {imię} zostaje puste). Kampania marketingowa trafi tylko do kont ze zgodą na newsletter.',
    manualEmailsPlaceholder: 'anna.nowak@example.com\njan.kowalski@example.com',
    invalidEmails: (emails: string[]) =>
      `Niepoprawne adresy: ${emails.slice(0, 5).join(', ')}${emails.length > 5 ? ` i ${emails.length - 5} innych` : ''}.`,
    audience: (n: number) => `Odbiorcy: ${n}`,
    excluded: (n: number) => `Pominięto ${n} bez zgody na newsletter.`,
    create: 'Utwórz kampanię',
    save: 'Zapisz zmiany',
    saved: 'Zapisano.',
    remove: 'Usuń szkic',
    templates: 'Szablony',
    useTemplate: 'Zacznij od szablonu',
    saveTemplate: 'Zapisz treść jako szablon',
    templateName: 'Nazwa szablonu',
    templateSaved: 'Szablon zapisany.',
    removeTemplate: 'Usuń szablon',
    previewTitle: 'Podgląd i próba',
    preview: 'Pokaż podgląd',
    previewFor: (email: string) => `Podgląd dla ${email}`,
    sendTest: 'Wyślij próbny mail do mnie',
    testSent: (email: string) => `Próbny mail wysłany na ${email}.`,
    sendTitle: 'Wysyłka',
    sendIntro: 'Odbiorcy zostaną ustaleni w chwili startu wysyłki. Maile idą przez kolejkę w tempie dozwolonym przez dostawcę poczty.',
    sendNow: 'Wyślij teraz',
    schedule: 'Zaplanuj',
    scheduleDate: 'Data wysyłki',
    scheduleTime: 'Godzina',
    sendConfirm: (n: number) => `Wysłać kampanię do ${n} odbiorców? Tego nie da się cofnąć.`,
    scheduledFor: (when: string) => `Zaplanowana na ${when}.`,
    unschedule: 'Odwołaj zaplanowaną wysyłkę',
    pause: 'Wstrzymaj',
    resume: 'Wznów',
    progress: (sent: number, total: number | null, queued: number, failed: number) =>
      `Wysłane ${sent}${total === null ? '' : ` z ${total}`} · w kolejce ${queued} · błędy ${failed}`,
    started: (when: string, who: string | null) => `Start wysyłki: ${when}${who ? ` (zlecił: ${who})` : ''}`,
    excludedAtStart: (n: number) => `Przy starcie pominięto ${n} kont bez zgody na newsletter.`,
    readOnly: 'Kampania została zlecona do wysyłki – treści i odbiorców nie można już zmienić.',
    showLog: 'Maile tej kampanii w logu wysyłek',
  },
  newsletter: {
    title: 'Wypis z newslettera',
    working: 'Wypisujemy…',
    unsubscribed:
      'Gotowe – nie będziesz już dostawać informacji o kolejnych edycjach. Maile organizacyjne dotyczące edycji, w których masz zapisy, nadal będą przychodzić.',
    already: 'Ten adres jest już wypisany z newslettera.',
    invalid: 'Link wypisu jest nieprawidłowy albo niepełny. Skopiuj cały adres z maila.',
  },
  editor: {
    bold: 'Pogrubienie',
    italic: 'Kursywa',
    h3: 'Nagłówek',
    h4: 'Mniejszy nagłówek',
    bulletList: 'Lista punktowana',
    orderedList: 'Lista numerowana',
    link: 'Wstaw link',
    unlink: 'Usuń link',
    linkPlaceholder: 'https://…',
    linkSave: 'Zapisz',
    linkNewTab: 'Otwieraj w nowej karcie',
    linkNewTabRemove: 'Otwieraj w tej karcie',
    color: 'Kolor tekstu',
    unsetColor: 'Usuń kolor',
    colorCancel: 'Anuluj',
    colorPicker: 'Dowolny kolor',
    colorPalette: 'Paleta kolorów',
    colorSave: 'Zastosuj',
    colorLabel: (color: string) => `Kolor tekstu ${color}`,
  },
  qr: {
    navTitle: 'Kod QR',
    whichEdition: 'Na którą edycję przychodzicie?',
    title: 'Kod QR na wejście',
    intro: 'Pokaż ten kod obsłudze przy wejściu – odprawi całą rodzinę naraz. Kod jest też w mailu z planem.',
    people: 'Osoby z rodziny',
    alt: (edition: string) => `Kod QR rodziny – ${edition}`,
    download: 'Pobierz obrazek',
    print: 'Drukuj',
  },
  checkIn: {
    nav: 'Punkt rejestracji',
    title: 'Rejestracja uczestników',
    chooseEdition: 'Wybierz edycję',
    chooseLocation: 'Wybierz właściwą lokalizację – tę, w której pracujesz dziś na punkcie rejestracji.',
    noEditions: 'Brak aktywnych edycji.',
    scan: 'Skanuj kod QR',
    stopScan: 'Zamknij kamerę',
    cameraError: 'Nie udało się uruchomić kamery. Zezwól na dostęp do kamery albo wyszukaj rodzinę ręcznie.',
    scanHint: 'Nakieruj kamerę na kod rodziny.',
    otherEdition: (edition: string) => `Ten kod dotyczy edycji ${edition}.`,
    invalid: 'Kod nieważny. Wyszukaj rodzinę ręcznie.',
    notOurCode: 'To nie jest kod rodziny z Koderka.',
    or: 'lub',
    scanTitle: 'Kod QR rodziny',
    scanLead: 'Rodzina ma kod QR z maila lub ze strony edycji – poproś o pokazanie kodu i zeskanuj go.',
    search: 'Wyszukaj uczestników',
    searchHint: 'Nazwisko lub imię opiekuna albo dziecka, e‑mail opiekuna (co najmniej 3 znaki).',
    none: 'Brak wyników.',
    notRegistered: 'bez zapisów',
    checkedIn: (n: number) => `odprawieni: ${n}`,
    onSiteTitle: 'Rodzina bez konta',
    onSiteLead: 'Załóż konto na miejscu – rodzina zostanie od razu odprawiona.',
    onSite: 'Utwórz konto uczestnikowi',
    closed: 'Odprawa jest możliwa od dnia przed wydarzeniem do końca dnia wydarzenia.',
    back: 'Wróć do punktu rejestracji',
    checkedInAt: (time: string, who: string | null) => `Odprawiony ${time}${who ? `, ${who}` : ''}`,
    notCheckedIn: 'nieodprawiony',
    todayNone: 'bez zajęć',
    all: 'Wszyscy',
    submit: 'Zatwierdź',
    saved: 'Zapisano odprawę.',
    noChanges: 'Brak zmian do zapisania.',
    offline: 'Brak połączenia – zaznaczenia zostały na ekranie. Zatwierdź ponownie, gdy wróci sieć.',
    addPerson: 'Dodaj osobę',
    addPersonTitle: 'Nowa osoba w rodzinie',
    addToFamily: 'Dodaj do rodziny',
    enrollments: 'Zapisy na zajęcia',
    guardian: 'opiekun',
    accountOwner: 'właściciel konta',
    speaker: 'Prowadzący',
    speakerTitle: 'Konto prowadzącego zajęcia',
    speakerLead: 'Ta osoba prowadzi dziś zajęcia:',
  },
  onSite: {
    title: 'Rejestracja rodziny na miejscu',
    intro:
      'Załóż konto rodzinie, która przyszła bez konta. Rodzina zostanie od razu odprawiona, a na e‑mail pójdzie link do ustawienia hasła.',
    guardian: 'Opiekun',
    children: 'Dzieci',
    addChild: 'Dodaj dziecko',
    removeChild: 'Usuń',
    consents: 'Zgody – zaznacza opiekun',
    consentsHint: 'Podaj urządzenie opiekunowi – zgody zapiszą się z adnotacją, kto z obsługi je przyjął.',
    submit: 'Załóż konto i odpraw',
    exists: 'Konto z tym adresem e‑mail już istnieje.',
    openExisting: 'Otwórz to konto',
  },
  attendance: {
    nav: 'Obecność na zajęciach',
    speakerNav: 'Moje zajęcia',
    title: 'Obecność na zajęciach',
    chooseEdition: 'Wybierz edycję',
    search: 'Szukaj zajęć',
    searchHint: 'Symbol, tytuł albo sala.',
    none: 'Brak zajęć na Twojej liście.',
    noSpeakerEditions: 'Nie prowadzisz zajęć w żadnej nadchodzącej edycji.',
    noEnrollment: 'bez zapisów',
    cancelled: 'odwołane',
    counter: (attended: number, enrolled: number, capacity: number) => `obecni ${attended} / zapisani ${enrolled} / pojemność ${capacity}`,
    back: 'Wróć do listy zajęć',
    opensAt: (time: string) => `Obecność można zaznaczać od ${time}.`,
    empty: 'Nikt nie jest zapisany na te zajęcia.',
    checkedIn: 'odprawiony na wejściu',
    notCheckedIn: 'nieodprawiony',
    present: 'Obecny',
    offline: 'Brak połączenia – obecność nie została zapisana. Spróbuj ponownie.',
    addTitle: 'Dopisz osobę na miejscu',
    addHint: 'Osoba odprawiona na wejściu albo z zapisami w tej edycji (imię lub nazwisko, co najmniej 3 znaki). Zapis sprawdza wiek, kolizje i wolne miejsca.',
    addSearch: 'Szukaj osoby',
    add: 'Zapisz i zaznacz obecność',
    alreadyEnrolled: 'już zapisana',
    notRegisteredHint: 'Nie ma tej osoby na liście? Najpierw odprawa na wejściu.',
    confirm: 'Potwierdź listę',
    confirmHint: 'Gdy nikt z zapisanych nie przyszedł – w statystykach będzie „0 obecnych”, a nie „nie potwierdzono”.',
    confirmed: 'Lista potwierdzona.',
    print: 'Drukuj listę',
    printName: 'Imię i nazwisko',
    printAge: 'Wiek',
    printPresent: 'Obecny',
  },
  staff: {
    title: 'Obsługa – konta rodzin',
    search: 'Szukaj po e‑mailu albo nazwisku opiekuna lub dziecka',
    searchHint: 'Wpisz co najmniej 2 znaki.',
    none: 'Brak wyników.',
    notVerified: 'niepotwierdzone',
    people: (n: number) => `osoby: ${n}`,
    backToSearch: 'Wróć do wyszukiwania',
    editions: 'Aktywne edycje',
  },
  status: {
    title: 'Stan systemu',
    checking: 'Sprawdzam połączenie z serwerem…',
    ok: 'Serwer i baza danych działają.',
    databaseDown: 'Serwer działa, ale baza danych nie odpowiada.',
    unreachable: 'Brak połączenia z serwerem.',
    version: 'Wersja',
  },
  fields: {
    email: 'Adres e‑mail',
    password: 'Hasło',
    newPassword: 'Nowe hasło',
    passwordRepeat: 'Powtórz hasło',
    passwordMismatch: 'Hasła nie są takie same.',
    currentPassword: 'Aktualne hasło',
    firstName: 'Imię',
    lastName: 'Nazwisko',
    school: 'Moja szkoła/szkoły (opcjonalnie)',
    referralSource: 'Skąd wiesz o konferencji? (opcjonalnie)',
    passwordHint: 'Co najmniej 8 znaków, w tym cyfra i wielka litera.',
  },
  register: {
    title: 'Załóż konto',
    intro: 'Jedno konto wystarczy na wszystkie edycje. Po rejestracji dodasz dzieci i zgłosisz udział.',
    consentsTitle: 'Zgody',
    required: '(wymagana)',
    submit: 'Załóż konto',
    haveAccount: 'Masz już konto?',
    doneTitle: 'Sprawdź skrzynkę',
    doneText: (email: string) =>
      `Wysłaliśmy wiadomość na ${email}. Kliknij link w mailu, aby aktywować konto. Jeśli nie widzisz maila, zajrzyj do spamu.`,
  },
  activate: {
    title: 'Aktywacja konta',
    working: 'Aktywuję konto…',
    done: 'Konto jest aktywne. Witamy!',
    alreadyActive: 'Konto jest już aktywne. Zaloguj się.',
    expired: 'Link wygasł albo został zastąpiony nowszym. Wyślij nowy link.',
    invalid: 'Link jest nieprawidłowy. Sprawdź, czy skopiowałeś cały adres z maila.',
    missingToken: 'Brak tokenu w linku.',
  },
  resend: {
    title: 'Wyślij link aktywacyjny ponownie',
    submit: 'Wyślij link',
    done: 'Jeśli konto czeka na aktywację, wysłaliśmy nowy link. Sprawdź skrzynkę.',
  },
  login: {
    title: 'Zaloguj się',
    submit: 'Zaloguj się',
    forgot: 'Nie pamiętam hasła',
    noAccount: 'Nie masz konta?',
    notVerified: 'Potwierdź adres e‑mail, aby się zalogować. Nie masz maila?',
  },
  forgot: {
    title: 'Nie pamiętam hasła',
    intro: 'Podaj adres e‑mail konta. Wyślemy link do ustawienia nowego hasła.',
    submit: 'Wyślij link',
    done: 'Jeśli konto istnieje, wysłaliśmy link. Link jest jednorazowy i ważny przez ograniczony czas.',
  },
  setPassword: {
    titleReset: 'Ustaw nowe hasło',
    titleInvitation: 'Ustaw hasło do konta',
    submit: 'Zapisz hasło',
    expired: 'Link wygasł albo został już użyty. Poproś o nowy.',
  },
  account: {
    title: 'Moje konto',
    dataTitle: 'Dane konta',
    emailInfo: 'Adresu e‑mail nie można zmienić samodzielnie – napisz do organizatorów.',
    save: 'Zapisz zmiany',
    saved: 'Zapisano.',
    passwordTitle: 'Zmiana hasła',
    changePassword: 'Zmień hasło',
    passwordChanged: 'Hasło zmienione. Pozostałe urządzenia zostały wylogowane.',
    roles: 'Role',
    student: 'Jestem uczniem',
    studentHint: 'Zaznacz, jeśli masz mniej niż 18 lat – zapiszesz się na zajęcia dla swojego wieku.',
    birthYearRequired: 'Podaj rok urodzenia.',
  },
  roles: { admin: 'administrator', staff: 'obsługa', speaker: 'prowadzący' },
  errors: {
    validation_error: 'Sprawdź poprawność pól formularza.',
    unauthorized: 'Zaloguj się, aby kontynuować.',
    forbidden: 'Nie masz uprawnień do tej operacji.',
    not_found: 'Nie znaleziono.',
    csrf_rejected: 'Żądanie zostało odrzucone. Odśwież stronę.',
    invalid_credentials: 'Nieprawidłowy e‑mail lub hasło.',
    email_not_verified: 'Potwierdź adres e‑mail, aby się zalogować.',
    too_many_attempts: 'Zbyt wiele prób, spróbuj później.',
    token_invalid: 'Link jest nieprawidłowy.',
    token_expired: 'Link wygasł albo został już użyty.',
    already_active: 'Konto jest już aktywne.',
    consent_outdated: 'Treść zgód się zmieniła. Odśwież stronę i zaznacz zgody ponownie.',
    consent_required: 'Zaznacz wymagane zgody.',
    wrong_password: 'Aktualne hasło jest nieprawidłowe.',
    participant_name_taken: 'Na koncie jest już osoba o tym imieniu i nazwisku.',
    last_admin: 'Nie można odebrać roli ostatniemu administratorowi.',
    edition_slug_taken: 'Ten identyfikator jest już zajęty przez inną edycję.',
    edition_slug_locked: 'Identyfikatora nie można zmienić po publikacji.',
    edition_status_transition: 'Takiej zmiany statusu nie można wykonać.',
    edition_has_registrations: 'W edycji są już zapisy – nie można cofnąć publikacji.',
    edition_not_started: 'Edycję można zakończyć od dnia wydarzenia.',
    edition_date_conflict: 'Zmiana daty lub miasta złamałaby reguły dla osób zapisanych w tej edycji:',
    edition_not_published: 'Ta edycja nie jest jeszcze opublikowana.',
    edition_finished: 'Ta edycja jest zakończona.',
    registration_not_open: 'Zapisy na tę edycję jeszcze się nie rozpoczęły.',
    registration_same_day: 'Ta osoba ma zapisy albo odprawę tego samego dnia w edycji w innym mieście:',
    participant_age_range: 'Dziecko musi mieć 3–18 lat w roku edycji:',
    participant_birth_year: 'Rok urodzenia dziecka jest poza dozwolonym zakresem.',
    participant_guardian: 'Tej operacji nie można wykonać na Twoich danych jako opiekuna.',
    participant_age_conflict: 'Po zmianie osoba nie pasowałaby wiekiem do edycji lub zajęć, na które jest zapisana. Najpierw ją wypisz:',
    early_access_invalid: 'Link jest nieaktualny.',
    room_name_taken: 'Sala o tej nazwie już istnieje w edycji.',
    room_has_activities: 'W sali są zajęcia – nie można jej usunąć.',
    activity_type_name_taken: 'Ta nazwa jest już używana przez inny rodzaj zajęć.',
    activity_type_in_use: 'Ten rodzaj jest używany przez zajęcia z zapisami:',
    activity_type_unknown: 'Nieznany rodzaj zajęć.',
    activity_symbol_taken: 'Ten symbol jest już zajęty w edycji.',
    activity_outside_event: 'Zajęcia muszą mieścić się w godzinach wydarzenia.',
    activity_conflict: 'Zmiana złamałaby istniejące zapisy – najpierw wypisz te osoby:',
    activity_not_cancelled: 'Zajęcia nie są odwołane.',
    activity_cancelled: 'Zajęcia są już odwołane.',
    activity_has_history: 'Na te zajęcia ktoś się już zapisywał – można je tylko odwołać.',
    import_invalid: 'Niepoprawny format importu.',
    enrollment_unavailable: 'Zapis niemożliwy.',
    enrollment_started: 'Zajęcia się rozpoczęły – wypis jest niemożliwy.',
    participant_enrolled_started: 'Osoba jest zapisana na zajęcia, które już się rozpoczęły – skontaktuj się z obsługą:',
    plan_mail_too_soon: 'Plan został wysłany przed chwilą – spróbuj ponownie za kilka minut.',
    reminder_invalid_time: 'Termin przypomnienia musi być w przyszłości i przed rozpoczęciem wydarzenia (i nie może się powtarzać).',
    reminder_locked: 'Ten termin już nadszedł – można go tylko podejrzeć.',
    campaign_locked: 'Kampanię można zmieniać tylko przed wysyłką.',
    campaign_status: 'Tej operacji nie można wykonać w obecnym stanie kampanii. Odśwież stronę.',
    no_recipients: 'Kampania nie ma odbiorców – zmień kryteria.',
    mail_not_resendable: 'Ponownie można wysłać tylko mail ze statusem „błąd”.',
    qr_invalid: 'Kod nieważny.',
    check_in_closed: 'Odprawa jest możliwa od dnia przed wydarzeniem do końca dnia wydarzenia.',
    participant_checked_in: 'Osoba została już odprawiona na wejściu – najpierw obsługa musi cofnąć odprawę:',
    account_exists: 'Konto z tym adresem e‑mail już istnieje.',
    settings_conflict: 'Nie można włączyć blokady – te osoby mają już zapisy tego samego dnia w edycjach w różnych miastach:',
    attendance_closed: 'Obecność można potwierdzać od 30 min przed zajęciami do końca dnia wydarzenia.',
    internal_error: 'Coś poszło nie tak. Spróbuj ponownie za chwilę.',
    network: 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.',
  } satisfies Record<ErrorCode | 'network', string>,
  board: {
    title: 'Najbliższe zajęcia',
    time: 'Start',
    activity: 'Zajęcia',
    room: 'Sala',
    age: 'Dla kogo',
    status: 'Kiedy',
    seats: 'Miejsca',
    ongoing: 'Trwa',
    now: 'Za chwilę',
    inMinutes: (m: number) => `Za ${m} min`,
    inHours: (h: number, m: number) => (m ? `Za ${h} h ${m} min` : `Za ${h} h`),
    cancelled: 'Odwołane',
    freeEntry: 'Wstęp wolny',
    free: (n: number) => `Wolne: ${n}`,
    full: 'Brak miejsc',
    adults: 'Dorośli',
    everyone: 'Wszyscy',
    kids: 'Dzieci',
    ageRange: (min: number, max: number) => `${min}–${max} lat`,
    ageFrom: (min: number) => `od ${min} lat`,
    ageTo: (max: number) => `do ${max} lat`,
    withAdults: 'i dorośli',
    openNow: 'Otwarte teraz',
    signUp: 'Zapisy i cała agenda:',
    page: (n: number, of: number) => `${n} / ${of}`,
    finished: 'Na dziś to już wszystkie zajęcia. Dziękujemy!',
    empty: 'Program pojawi się wkrótce.',
    loading: 'Ładowanie programu…',
    notFound: 'Tablica ruszy po opublikowaniu edycji.',
    offline: (time: string) => `Brak połączenia – dane z ${time}`,
    preview: (time: string) => `Podgląd: ${time}`,
  },
  notFound: {
    title: 'Nie ma takiej strony',
    back: 'Wróć na stronę główną',
  },
} as const;
